/**
 * @fileoverview Audio transcription service using OpenAI Whisper via backend API
 * @module services/transcription-service
 * @description Provides audio recording and transcription functionality
 */

import { api } from '@/utils/api-client';
import { logger } from '@/lib/logger'

/**
 * Transcription result from the backend API
 */
export interface TranscriptionResult {
  /** Transcribed text */
  text: string;
  /** Audio duration in seconds */
  duration?: number;
  /** Detected language */
  language?: string;
  /** Processing time in milliseconds */
  processingTimeMs?: number;
}

/**
 * Transcription API response
 */
interface TranscriptionResponse {
  success: boolean;
  data?: TranscriptionResult;
  error?: string;
}

/**
 * Convert AudioBuffer to WAV format
 * This is necessary because Gemini doesn't support webm format for transcription
 * WAV is universally supported by all transcription APIs
 *
 * @param buffer - AudioBuffer to convert
 * @returns Blob containing WAV audio data
 */
function audioBufferToWav(buffer: AudioBuffer): Blob {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const format = 1; // PCM
  const bitDepth = 16;

  const bytesPerSample = bitDepth / 8;
  const blockAlign = numChannels * bytesPerSample;

  const dataLength = buffer.length * blockAlign;
  const bufferLength = 44 + dataLength; // 44 bytes for WAV header

  const arrayBuffer = new ArrayBuffer(bufferLength);
  const view = new DataView(arrayBuffer);

  // WAV header
  const writeString = (offset: number, string: string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  let offset = 0;
  writeString(offset, 'RIFF'); offset += 4;
  view.setUint32(offset, 36 + dataLength, true); offset += 4;
  writeString(offset, 'WAVE'); offset += 4;
  writeString(offset, 'fmt '); offset += 4;
  view.setUint32(offset, 16, true); offset += 4;
  view.setUint16(offset, format, true); offset += 2;
  view.setUint16(offset, numChannels, true); offset += 2;
  view.setUint32(offset, sampleRate, true); offset += 4;
  view.setUint32(offset, sampleRate * blockAlign, true); offset += 4;
  view.setUint16(offset, blockAlign, true); offset += 2;
  view.setUint16(offset, bitDepth, true); offset += 2;
  writeString(offset, 'data'); offset += 4;
  view.setUint32(offset, dataLength, true); offset += 4;

  // Write audio data
  const floatTo16BitPCM = (output: DataView, offset: number, input: Float32Array) => {
    for (let i = 0; i < input.length; i++, offset += 2) {
      const s = Math.max(-1, Math.min(1, input[i]));
      output.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
  };

  for (let channel = 0; channel < numChannels; channel++) {
    floatTo16BitPCM(view, 44 + channel * 2, buffer.getChannelData(channel));
  }

  return new Blob([arrayBuffer], { type: 'audio/wav' });
}

/**
 * Convert an audio blob to WAV format using Web Audio API
 * This ensures compatibility with Gemini transcription API
 *
 * @param blob - Audio blob to convert
 * @returns Promise resolving to WAV audio blob
 */
async function convertToWav(blob: Blob): Promise<Blob> {
  const arrayBuffer = await blob.arrayBuffer();
  const audioContext = new AudioContext({ sampleRate: 16000 }); // Gemini downsamples to 16kHz anyway
  const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

  return audioBufferToWav(audioBuffer);
}

/**
 * Transcribe audio blob using the backend Whisper API
 *
 * @param audioBlob - Audio blob to transcribe
 * @param language - Optional language code (e.g., "en", "ar")
 * @returns Promise resolving to transcription result
 */
export async function transcribeAudio(
  audioBlob: Blob,
  language?: string
): Promise<TranscriptionResult> {
  // Convert to WAV format for Gemini compatibility
  // Gemini doesn't support webm format for transcription
  // See: https://ai.google.dev/gemini-api/docs/audio
  let wavBlob = audioBlob;
  if (audioBlob.type !== 'audio/wav') {
    try {
      wavBlob = await convertToWav(audioBlob);
    } catch (error) {
      logger.error('Failed to convert audio to WAV', error);
      // Fall back to original blob if conversion fails
      wavBlob = audioBlob;
    }
  }

  const formData = new FormData();
  formData.append('audio', wavBlob, 'recording.wav');

  if (language) {
    formData.append('language', language);
  }

  const data: TranscriptionResponse = await api.post('/transcription', formData);

  if (!data.success || !data.data) {
    throw new Error(data.error || 'Transcription failed');
  }

  return data.data;
}

/**
 * Audio recorder class for managing audio recording sessions
 */
export class AudioRecorder {
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private stream: MediaStream | null = null;

  /**
   * Check if audio recording is supported in the browser
   */
  static isSupported(): boolean {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && window.MediaRecorder);
  }

  /**
   * Start recording audio from the microphone
   *
   * @returns Promise that resolves when recording starts
   * @throws Error if microphone access is denied or unavailable
   */
  async start(): Promise<void> {
    if (!AudioRecorder.isSupported()) {
      throw new Error('Audio recording is not supported in this browser');
    }

    // Request microphone access
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      });
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          throw new Error('Microphone access denied. Please allow microphone access and try again.');
        }
        if (error.name === 'NotFoundError') {
          throw new Error('No microphone found. Please connect a microphone and try again.');
        }
      }
      throw new Error('Failed to access microphone');
    }

    // Reset audio chunks
    this.audioChunks = [];

    /**
     * Choose the best audio format for the browser
     * The audio will be converted to WAV on the client side before sending to the API
     * This ensures compatibility with Gemini transcription API
     *
     * Supported formats by browsers:
     * - Chrome: audio/webm;codecs=opus, audio/webm
     * - Firefox: audio/webm;codecs=opus, audio/webm, audio/ogg
     * - Safari: audio/mp4, audio/aac
     */
    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : MediaRecorder.isTypeSupported('audio/webm')
      ? 'audio/webm'
      : MediaRecorder.isTypeSupported('audio/ogg')
      ? 'audio/ogg'
      : MediaRecorder.isTypeSupported('audio/mp4')
      ? 'audio/mp4'
      : '';  // Let browser decide

    const mediaRecorderOptions: MediaRecorderOptions = mimeType
      ? { mimeType, audioBitsPerSecond: 128000 }
      : { audioBitsPerSecond: 128000 };

    this.mediaRecorder = new MediaRecorder(this.stream, mediaRecorderOptions);

    // Collect audio data
    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        this.audioChunks.push(event.data);
      }
    };

    // Start recording with 1 second timeslices
    this.mediaRecorder.start(1000);
  }

  /**
   * Stop recording and return the audio blob
   *
   * @returns Promise resolving to the recorded audio blob
   */
  async stop(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder) {
        reject(new Error('No recording in progress'));
        return;
      }

      this.mediaRecorder.onstop = () => {
        // Stop all tracks in the stream
        if (this.stream) {
          this.stream.getTracks().forEach((track) => track.stop());
          this.stream = null;
        }

        // Combine all chunks into a single blob
        const audioBlob = new Blob(this.audioChunks, {
          type: this.mediaRecorder?.mimeType || 'audio/webm',
        });

        this.audioChunks = [];
        this.mediaRecorder = null;

        resolve(audioBlob);
      };

      this.mediaRecorder.onerror = (event) => {
        reject(new Error('Recording error occurred'));
      };

      // Stop recording
      this.mediaRecorder.stop();
    });
  }

  /**
   * Cancel recording without returning data
   */
  cancel(): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }

    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }

    this.audioChunks = [];
    this.mediaRecorder = null;
  }

  /**
   * Check if currently recording
   */
  isRecording(): boolean {
    return this.mediaRecorder?.state === 'recording';
  }
}
